import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";

import quizInstanceAdminStatusContext from '../context/quizInstanceAdminStatusContext';
import QuizApi from '../QuizApi';
import QuizAdminAnswerTextList from './QuizAdminAnswerTextList';
import QuizWordCloud from './QuizWordCloud';

function getAnswersByQuestionId(curVal, newAnswerList) {

  const nextVal = {};

  // Group the answer list by question
  for (const answer of newAnswerList)
  {
    const key = answer.questionId;
    if (!nextVal[key])
    {
      nextVal[key] = [];
    }

    nextVal[key].push(answer);
  }

  // Concatenate with existing answer lists, if any
  for (const key of Object.keys(nextVal))
  {
    if (curVal && curVal[key])
    {
      nextVal[key] = curVal[key].concat(nextVal[key]);
      nextVal[key].sort((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1);
      nextVal[key].sort((a, b) => a.id > b.id ? 1 : -1);
    }
  }

  return Object.assign({}, curVal, nextVal);
}

export default function QuizAdminStatsFree() {

  const ctx = useContext(quizInstanceAdminStatusContext);
  const [answersByQuestionId, setAnswersByQuestionId] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const afterIdRef = useRef(0);

  // ============================
  // Callback to fetch answers
  // ============================
  const fetchAnswerList = useCallback(() => {

    const fn = async () => 
    {
      const result = await QuizApi.quizAdminGetAnswerList(ctx.quizAccessShortId, afterIdRef.current);
      if (result && result.response && result.response.answerList)
      {
        const { answerList } = result.response;

        if (answerList && answerList.length)
        {
          // Always update the ?after query parameter
          afterIdRef.current = answerList[answerList.length - 1].id;
        }

        setAnswersByQuestionId(curVal => {

          if (!curVal && !ctx.quizInfo.completed)
          {
            // This is the first time we're fetching the answer list, and the quiz is not completed.
            // On first render, we don't want to show previously sent answers, only new ones.
            return {};
          }

          return getAnswersByQuestionId(curVal, answerList)
        });

      }
    };

    fn();

  }, [ctx, afterIdRef]);

  // ================================
  // Effect to trigger answer fetch
  // ================================
  useEffect(() => {

    if (!ctx.quizInfo.completed)
    {
      // Fetch answers using the current "after" value
      fetchAnswerList();
    }
    else
    {
      // Fetch the entire answer list (reset "after" value)
      setAnswersByQuestionId(null);
      afterIdRef.current = 0;
      fetchAnswerList();
    }

  }, [ctx, fetchAnswerList]);

  return (
    <>
      {answersByQuestionId === null &&         
        <Container>
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        </Container>
      }
      {answersByQuestionId !== null &&
        <Container id="freeTextAdminStatsWrapper" className="pt-4 h-100" fluid="md">
          <Row className="mb-3 ml-1">
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${viewMode === 'list' ? 'btn-quizPrimaryDark' : 'btn-outline-secondary'}`}
                onClick={() => setViewMode('list')}>
                Textlista
              </button>
              <button
                type="button"
                className={`btn ${viewMode === 'wordcloud' ? 'btn-quizPrimaryDark' : 'btn-outline-secondary'}`}
                onClick={() => setViewMode('wordcloud')}>
                Ordmoln
              </button>
            </div>
          </Row>
          <Row className="h-100">
            {ctx.quizInfo.questionList.map(question => {

              const answerList = answersByQuestionId[question.id] || [];
              const keepAll = ctx.quizInfo.config && ctx.quizInfo.config.freeTextResultMode === "keepAll";

              const color = ctx.quizStyle.colorList[(question.order - 1) % ctx.quizStyle.colorList.length];

              return (
                <Col key={question.id} className="mr-4 h-100">
                  {viewMode === 'list' ? (
                    <QuizAdminAnswerTextList
                      question={question}
                      answerList={answerList}
                      shouldFadeAnswers={!keepAll && !ctx.quizInfo.completed}
                    />
                  ) : (
                    <>
                      <div className="text-center quizQuestionHeader" style={{ backgroundColor: color.primaryCss, color: '#fff', borderRadius: '0px 0px 13px 0px' }}>
                        <h4 className="quizQuestionHeaderText" style={{ padding: '10px 16px' }}>FRÅGA {question.order}</h4>
                      </div>
                      <div className="quizAdminAnswerTextListQuestion text-dark mt-2" style={{ borderColor: color.primaryCss }}>
                        <h3 className="quizQuestionText">{question.markdown}</h3>
                      </div>
                      <QuizWordCloud answerList={answerList} colorList={ctx.quizStyle.colorList} />
                    </>
                  )}
                </Col>
              );
            })}
          </Row>
        </Container>
      }
    </>
  );
};